import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/layout/Header';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { CreditCard, Calendar, Shield, Star, Crown, Building, ExternalLink, AlertTriangle, CheckCircle } from 'lucide-react';
import { billingApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const PLAN_LABELS = {
  free: { name: 'Free', icon: Building, color: 'bg-slate-100 text-slate-700' },
  silver: { name: 'Silver', icon: Star, color: 'bg-slate-100 text-slate-600' },
  gold: { name: 'Gold', icon: Shield, color: 'bg-amber-100 text-amber-800' },
  vip: { name: 'VIP', icon: Crown, color: 'bg-purple-100 text-purple-800' },
};

const STATUS_LABELS = {
  active: { label: 'Active', color: 'bg-green-100 text-green-800' },
  trialing: { label: 'Trial', color: 'bg-blue-100 text-blue-800' },
  past_due: { label: 'Past Due', color: 'bg-red-100 text-red-800' },
  canceled: { label: 'Canceled', color: 'bg-slate-100 text-slate-600' },
  none: { label: 'No subscription', color: 'bg-slate-100 text-slate-500' },
};

const BillingPage = () => {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [canceling, setCanceling] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    fetchBilling();
  }, []);

  const fetchBilling = async () => {
    try {
      const { data } = await billingApi.getMyBilling();
      setBilling(data);
    } catch (err) {
      console.error('Billing fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleManageBilling = async () => {
    setPortalLoading(true);
    try {
      const { data } = await billingApi.createPortal();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(msg || 'Could not open billing portal.');
    } finally {
      setPortalLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will retain access until the end of your current billing period.')) return;
    setCanceling(true);
    try {
      await billingApi.cancel(true);
      toast.success('Subscription will be canceled at end of billing period.');
      await fetchBilling();
      await checkAuth();
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(msg || 'Could not cancel subscription.');
    } finally {
      setCanceling(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const plan = PLAN_LABELS[billing?.membership || 'free'] || PLAN_LABELS.free;
  const status = STATUS_LABELS[billing?.membership_status || 'none'] || STATUS_LABELS.none;
  const PlanIcon = plan.icon;

  return (
    <div className="min-h-screen bg-[#F8F9FB]">
      <Header />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-6">
          <h1 className="text-xl font-bold text-[#0A192F]" data-testid="billing-title">Billing & Subscription</h1>
          <p className="text-sm text-slate-500">Manage your plan, billing, and payment details</p>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-white border border-slate-200 p-6 animate-pulse">
                <div className="h-5 bg-slate-200 rounded w-1/3 mb-3" />
                <div className="h-4 bg-slate-200 rounded w-2/3" />
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-5">
            {/* Current Plan */}
            <div className="bg-white border border-slate-200 p-6" data-testid="billing-current-plan">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-[#0A192F] flex items-center gap-2">
                  <CreditCard className="w-5 h-5" /> Current Plan
                </h2>
                <Badge className={status.color}>{status.label}</Badge>
              </div>

              <div className="flex items-center gap-3 mb-4">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${plan.color}`}>
                  <PlanIcon className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-lg font-bold text-[#0A192F]">{plan.name} Plan</p>
                  {billing?.membership !== 'free' && billing?.membership_status && (
                    <p className="text-xs text-slate-500">
                      {billing.membership_status === 'trialing' && billing.trial_ends_at
                        ? `Trial ends ${formatDate(billing.trial_ends_at)}`
                        : billing.current_period_end
                          ? `Renews ${formatDate(billing.current_period_end)}`
                          : ''}
                    </p>
                  )}
                </div>
              </div>

              {billing?.cancel_at_period_end && (
                <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4">
                  <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                  <p className="text-sm text-amber-800">
                    Your subscription will be canceled on {formatDate(billing.current_period_end)}. You retain access until then.
                  </p>
                </div>
              )}

              {billing?.membership === 'free' ? (
                <Button
                  className="bg-[#EA580C] hover:bg-[#C2410C] text-white font-semibold"
                  onClick={() => navigate('/membership')}
                  data-testid="billing-upgrade-btn"
                >
                  Upgrade Your Plan <ExternalLink className="w-4 h-4 ml-1.5" />
                </Button>
              ) : (
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => navigate('/membership')}
                    data-testid="billing-change-plan-btn"
                  >
                    Change Plan
                  </Button>
                  {billing?.stripe_customer_id && (
                    <Button
                      variant="outline"
                      onClick={handleManageBilling}
                      disabled={portalLoading}
                      data-testid="billing-manage-btn"
                    >
                      {portalLoading ? 'Loading...' : 'Manage Billing'}
                      <ExternalLink className="w-4 h-4 ml-1.5" />
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Subscription Details */}
            {billing?.membership !== 'free' && (
              <div className="bg-white border border-slate-200 p-6" data-testid="billing-details">
                <h2 className="font-semibold text-[#0A192F] flex items-center gap-2 mb-4">
                  <Calendar className="w-5 h-5" /> Subscription Details
                </h2>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Status</p>
                    <p className="font-medium text-[#0A192F] capitalize">{billing.membership_status || '-'}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Current Period Ends</p>
                    <p className="font-medium text-[#0A192F]">{formatDate(billing.current_period_end)}</p>
                  </div>
                  {billing.trial_ends_at && (
                    <div>
                      <p className="text-slate-500">Trial Ends</p>
                      <p className="font-medium text-[#0A192F]">{formatDate(billing.trial_ends_at)}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-slate-500">Cancel at Period End</p>
                    <p className="font-medium text-[#0A192F]">{billing.cancel_at_period_end ? 'Yes' : 'No'}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Cancel Subscription */}
            {billing?.membership !== 'free' && billing?.stripe_subscription_id && !billing?.cancel_at_period_end && (
              <div className="bg-white border border-slate-200 p-6" data-testid="billing-cancel-section">
                <h2 className="font-semibold text-[#0A192F] mb-2">Cancel Subscription</h2>
                <p className="text-sm text-slate-500 mb-4">
                  Cancel your subscription. You'll retain access to premium features until the end of your current billing period.
                </p>
                <Button
                  variant="outline"
                  className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                  onClick={handleCancel}
                  disabled={canceling}
                  data-testid="billing-cancel-btn"
                >
                  {canceling ? 'Canceling...' : 'Cancel Subscription'}
                </Button>
              </div>
            )}

            {/* Canceled confirmation */}
            {billing?.cancel_at_period_end && (
              <div className="bg-white border border-slate-200 p-6 text-center">
                <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                <p className="font-semibold text-[#0A192F]">Cancellation Scheduled</p>
                <p className="text-sm text-slate-500">Your plan will revert to Free on {formatDate(billing.current_period_end)}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BillingPage;
